import React, { createContext, useEffect, useRef, useState } from 'react';
import { FaCheck, FaChevronDown, FaXmark } from 'react-icons/fa6';
import { useIntl } from 'react-intl';
import { clsx } from 'clsx';

import { Input } from './Input';
import useClickOutside from '@/@core/hooks/useClickOutside';
import Error from './atoms/ErrorMessage/ErrorMessage';

interface Props<K> {
  reduceValue: (item: K) => string | number;
  reduceLabel: (item: K) => string;
  onChange: (item: K) => void;
  children?: (item: K) => React.ReactNode;
  className?: string;
  options: K[];
  field?: 'search' | 'select';
  value?: string | number;
  position?: 'to-top' | 'to-bottom';
  initSearch?: string;
  IconEnd?: React.ReactNode;
  id?: string;
  noClear?: boolean;
  small?: boolean;
  listItemSize?: string;
  iconSize?: string;
  placeholder?: string;
  readOnly?: boolean;
  keyFn?: (item: K) => string;
  error?: string | string[] | null;
}

interface OptionProps {
  children?: React.ReactNode;
  className?: string;
  onClick: () => void;
  checked?: boolean;
}

interface TSelectSearchContext {
  open: boolean;
  toggle: React.Dispatch<React.SetStateAction<boolean>>;
  onSelected: (value: any) => void;
}

const SelectSearchContext = createContext<TSelectSearchContext>({} as TSelectSearchContext);

export function SelectSearch<K>({
  children,
  reduceLabel,
  onChange,
  keyFn,
  options = [],
  field = 'search',
  position = 'to-bottom',
  initSearch = '',
  IconEnd,
  noClear = false,
  noPlaceholder = false,
  listItemSize,
  small = false,
  fullWidth = false,
  iconSize = '',
  placeholder = '',
  reduceValue,
  value,
  error,
  className,
  ...props
}: Props<K> & { noPlaceholder?: boolean; fullWidth?: boolean }) {
  const { formatMessage: $t } = useIntl();
  const [open, toggle] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const inputRef = useRef<HTMLDivElement | null>(null);
  const listItemRef = useRef<HTMLDivElement | null>(null);

  let filteredOptions = options || [];
  if (search && field === 'search') {
    filteredOptions = (options || []).filter((item) =>
      reduceLabel(item).toLocaleLowerCase().includes(search.toLocaleLowerCase()),
    );
  }

  useClickOutside(inputRef, () => {
    if (open) {
      const normalizeSearch = String(search).trim().toLocaleUpperCase();
      if (normalizeSearch == String(initSearch).trim().toLocaleUpperCase()) {
        toggle(false);
      } else {
        const item = filteredOptions.find(
          (option) => reduceLabel(option).trim().toLocaleUpperCase() == normalizeSearch,
        );
        if (filteredOptions.length && !!item) {
          onSelected(item);
          setSearch(normalizeSearch);
        } else {
          resetState();
        }
      }
    }
  });

  const onSelected = (item: any) => {
    onChange(item);
    setSearch(reduceLabel(item));
    toggle(false);
  };

  const resetState = () => {
    onChange({} as K);
    setSearch('');
    toggle(false);
  };

  const renderOption = (item: K) => children && children(item);

  useEffect(() => {
    if (value && open) {
      const item = listItemRef.current?.querySelector('[data-selected="true"]');
      if (item) {
        setTimeout(() => {
          item.scrollIntoView({ behavior: 'instant', block: 'center' });
        }, 0);
      }
    }
  }, [value, open]);

  useEffect(() => {
    if (value) {
      const option = options.find((option) => reduceValue(option) == value);
      if (option) {
        setSearch(reduceLabel(option));
      }
    }
  }, [options]);

  const cssListItemSizeClasses = clsx(
    'w-full flex flex-col border rounded overflow-y-auto scroll__styled absolute z-50 dark:border-slate-700 shadow',
    listItemSize ?? 'max-h-[250px]',
  );

  return (
    <div className={clsx(fullWidth ? 'w-full flex-1' : '')}>
      <div className={clsx('-z-1 h-full w-full', open ? 'relative' : '')} ref={inputRef}>
        <SelectSearchContext.Provider value={{ open, toggle, onSelected }}>
          <div className="flex h-full">
            <div className="group/item relative flex-1">
              <Input
                onFocus={() => toggle(true)}
                placeholder={
                  !!noPlaceholder
                    ? ' '
                    : field === 'search'
                      ? $t({ id: placeholder || 'Search' })
                      : `${$t({ id: 'Select' })}..`
                }
                value={search as string}
                onChange={(ev) => setSearch(ev.target.value.toLocaleUpperCase())}
                onBlur={(ev) => {
                  setTimeout(() => {
                    if (!String(ev.target.value).trim()) {
                      resetState();
                    }
                  }, 300);
                }}
                small={small}
                autoComplete="off"
                inputType="SELECT"
                className={clsx(className, { 'border-red-500': !!error })}
                {...props}
              />
              <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center gap-0.5 px-0.5 text-gray-400">
                {!noClear && (
                  <FaXmark
                    className={clsx(
                      'pointer-events-auto invisible relative cursor-pointer select-none text-sm text-red-600',
                      !!search.length && 'group-hover/item:visible',
                    )}
                    onClick={resetState}
                  />
                )}
                <FaChevronDown size={10} className={clsx(iconSize)} />
              </div>
            </div>
            {IconEnd}
          </div>
          {open && !!(filteredOptions || []).length && (
            <div
              ref={listItemRef}
              className={clsx(
                cssListItemSizeClasses,
                position === 'to-top' ? 'bottom-0' : 'top-full',
              )}
            >
              {!!children
                ? (filteredOptions || []).map((options, index) => (
                    <SelectSearch.Option
                      key={keyFn ? keyFn(options) : index}
                      onClick={() => onSelected(options)}
                      checked={reduceValue(options) == value}
                    >
                      {renderOption(options)}
                    </SelectSearch.Option>
                  ))
                : (filteredOptions || []).map((option, key) => (
                    <SelectSearch.Option
                      key={keyFn ? keyFn(option) : key}
                      onClick={() => onSelected(option)}
                      checked={reduceValue(option) == value}
                    >
                      {/* @ts-ignore */}
                      {option.name}
                    </SelectSearch.Option>
                  ))}
            </div>
          )}
          <Error message={error} />
        </SelectSearchContext.Provider>
      </div>
    </div>
  );
}

export function Option({ children, className, onClick, checked, ...props }: OptionProps) {
  return (
    <button
      className={clsx(
        'pointer-events-auto relative w-full flex-1 bg-white p-1 text-start text-xs hover:bg-gray-100 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700',
        className,
      )}
      onClick={onClick}
      type="button"
      data-selected={checked}
      {...props}
    >
      {children}

      {checked && (
        <FaCheck size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-600" />
      )}
    </button>
  );
}

SelectSearch.Option = Option;
